import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const FAQ = [
  {
    question: "What is solar forecasting software?",
    answer:
      "Solar forecasting software estimates future energy availability—for example, projected kWh at sunrise. Watt Tower focuses on overnight reserve forecasting: will the system have enough energy by morning? We show best, expected, and worst scenarios with explicit confidence (High, Medium, Low) so installers can act before the customer runs out.",
  },
  {
    question: "How accurate are the forecasts?",
    answer:
      "Forecasts depend on historical telemetry, load patterns, and system configuration. Confidence increases over 30–90 days as the system learns. We show confidence explicitly and never imply false precision. We bias conservative so you are not falsely reassured.",
  },
  {
    question: "Can I see fleet-level at-risk flags?",
    answer:
      "Yes. Watt Tower’s solar forecasting software flags “At Risk” at the fleet level. You see how many systems may run low by sunrise and can drill into any property for projected kWh and confidence. Alerts can be routed to you, the customer, or both.",
  },
  {
    question: "Does it work for off-grid systems?",
    answer:
      "Yes. Overnight reserve forecasting is especially relevant for off-grid and hybrid systems. We project energy available at sunrise and flag risk regardless of grid connection. Supported via Sol-Ark, EG4, and SolarEdge.",
  },
];

export default function SolarForecastingSoftware() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </Helmet>
      <article className="mx-auto max-w-3xl px-4 py-16 lg:px-8">
        <h1 className="mb-6 font-sans text-3xl font-bold md:text-4xl">
          Solar Forecasting Software for Installers
        </h1>
        <p className="mb-10 font-sans text-lg leading-relaxed text-wt-text-secondary">
          Solar forecasting software that projects overnight reserve risk:
          projected kWh at sunrise, confidence bands, and fleet-level at-risk
          flags. Built for installers managing residential solar and battery
          systems.
        </p>

        <section className="space-y-10">
          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Why Installers Need Overnight Forecasting
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            One of the biggest blind spots for residential solar and battery
            systems is whether there will be enough energy by sunrise. Solar
            forecasting software that focuses on overnight reserve gives
            installers advance visibility. Watt Tower projects kWh at
            sunrise with best, expected, and worst scenarios and explicit
            confidence (High, Medium, Low). Fleet-level “At Risk” flags let
            you prioritize follow-up or notifications instead of reacting
            after a 2 a.m. call.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Projected kWh at Sunrise, Not Vague Percentages
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Our solar forecasting software shows energy in kWh. You see
            projected kWh at sunrise per system, not just state-of-charge
            percent. Confidence is stated (High, Medium, Low); we never
            imply false precision. Forecasts improve over 30–90 days as the
            system learns load and PV patterns. We bias conservative so you
            are not falsely reassured.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Fleet-Level At-Risk Flags
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            The installer dashboard summarizes how many systems are at
            overnight reserve risk. Drill into any property for full forecast
            detail and confidence. Alerts can be routed to you, the customer,
            or both. Solar forecasting at fleet level replaces guesswork with
            data-backed prioritization.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Built for Sol-Ark and Hybrid Systems
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Watt Tower’s solar forecasting software supports Sol-Ark, EG4,
            and SolarEdge. Overnight reserve forecasting works for hybrid and
            off-grid residential systems. Read-only access; credentials
            encrypted and revocable. We observe; we do not command.
          </p>
        </section>

        <section className="mt-16 border-t border-wt-border-default pt-12">
          <h2 className="mb-8 font-sans text-2xl font-semibold text-wt-text-primary">
            Frequently Asked Questions
          </h2>
          <dl className="space-y-6">
            {FAQ.map((item) => (
              <div key={item.question}>
                <dt className="font-sans text-base font-semibold text-wt-text-primary">
                  {item.question}
                </dt>
                <dd className="mt-2 font-sans text-base leading-relaxed text-wt-text-secondary">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <a href="/#installer-access">
            <Button className="h-11 bg-primary px-6 font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted">
              Request Installer Access
            </Button>
          </a>
          <Link to="/">
            <Button
              variant="outline"
              className="h-11 border-wt-border-default bg-transparent font-sans text-wt-text-primary hover:bg-white/5"
            >
              Back to home
            </Button>
          </Link>
        </div>
        <p className="mt-6 font-sans text-sm text-wt-text-muted">
          See also:{" "}
          <Link
            to="/solar-monitoring-software-for-installers"
            className="text-primary hover:underline"
          >
            solar monitoring software for installers
          </Link>
          ,{" "}
          <Link
            to="/solar-fleet-dashboard"
            className="text-primary hover:underline"
          >
            solar fleet dashboard
          </Link>
          ,{" "}
          <Link
            to="/battery-backup-monitoring"
            className="text-primary hover:underline"
          >
            battery backup monitoring
          </Link>
          .
        </p>
      </article>
    </PublicLayout>
  );
}
